import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../Modal';
import { ModalWithImageWrapper, ModalImage, Box, ModalTextWrapper } from './EquipmentModal.styles';
import { Text } from '@oneaudi/unified-web-components';
import parse from 'react-html-parser';
import { SYS_SIZE_FIXED_700 } from '@oneaudi/unified-web-common';

type EquipmentModalProps = {
  copy: string;
  description?: string | null;
  image?: string | null;
  noImageUrl: boolean;
  alt: string;
  onClose: () => void;
};

const EquipmentModal: React.FC<EquipmentModalProps> = ({
  copy,
  image,
  description,
  noImageUrl,
  alt,
  onClose,
}) => {
  const { t } = useTranslation();
  const [isModalContentFocused, setIsModalContentFocused] = useState<boolean>(true);

  const renderDescriptionContent = (
    descriptionValue: string | null | undefined,
  ): React.ReactNode => {
    {
      /* We may receive an empty HTML list from the backend */
    }
    if (
      descriptionValue?.trim() === '<ul><li></li></ul>' ||
      descriptionValue?.trim() === '<ul><li/></ul>'
    ) {
      return null;
    }
    return (
      <Text
        tag="div"
        variant={{
          order: '1',
          type: 'copy',
          style: 'normal',
        }}
        data-testid="modal-text-description"
      >
        {parse(
          descriptionValue || t('featuresAndSpecsSection.categories.optionalEquipment.noDetails'),
        )}
      </Text>
    );
  };

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      // pressing TAB key switches focus between `ModalContainer` and `CloseModalButton` components
      if (event.key === 'Tab') {
        event.preventDefault();
        if (isModalContentFocused) {
          document.getElementById('modalContainerVDPFSS')?.blur();
          document.getElementById('closeModalVDPFSS')?.focus();
          setIsModalContentFocused(false);
        } else {
          document.getElementById('closeModalVDPFSS')?.blur();
          document.getElementById('modalContainerVDPFSS')?.focus();
          setIsModalContentFocused(true);
        }
      }
    },
    [isModalContentFocused],
  );

  useEffect(() => {
    // focus the ModalContainer right after the Modal is painted on the screen
    document.getElementById('modalContainerVDPFSS')?.focus();
    return () => {
      // focus the InfoIcon component after closing the modal
      document.getElementById('infoIconVDPFSS')?.focus();
    };
  }, []);

  return (
    <Modal onClose={onClose} width={!image || noImageUrl ? '600px' : '100%'} onKeyDown={onKeyDown}>
      <ModalWithImageWrapper data-testid="modal-equipment-wrapper">
        {image && !noImageUrl && (
          <Box>
            <ModalImage data-testid="modal-image" src={image} alt={alt} />
          </Box>
        )}
        <Box>
          <ModalTextWrapper>
            <Text
              variant={{
                order: '1',
                type: 'copy',
                style: 'bold',
              }}
              data-testid="modal-header"
              margin={[0, 0, SYS_SIZE_FIXED_700, 0]}
            >
              {parse(copy)}
            </Text>
            {/* It is possible that we get html code from the backend */}
            {renderDescriptionContent(description)}
          </ModalTextWrapper>
        </Box>
      </ModalWithImageWrapper>
    </Modal>
  );
};

export default EquipmentModal;
